import type { ReactNode } from "react";
import { CloseStatus } from "@common/types/closeStatus";
import type { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import type {
    BoardColumnDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewWorkspaceRow,
} from "../clientsWorkOverviewViewConfig";
import type { GroupBy } from "../useGroupedTableData";
import { isWorkPersonRole, WORK_PERSON_ROLES } from "../workPersonGroups";
import {
    getWorkDueDateGroups,
    getWorkTemplateGroups,
    type WorkRowGroup,
} from "../workRowGroups";
import {
    CANCELED_STAGE_GROUP_ID,
    DONE_STAGE_GROUP_ID,
    getWorkStageGroups,
} from "../workStageGroups";

/** What Board columns group by: List's grouping, where "none" and person groupings are By stage. */
export type WorkBoardColumnBy = "stage" | "client" | "template" | "due_date";

/** The groupings the header picker offers on Board, in its order. */
export const WORK_BOARD_COLUMN_BY_OPTIONS: readonly WorkBoardColumnBy[] = [
    "stage",
    "client",
    "template",
    "due_date",
];

/** The header picker's groupings on Board: a person grouping is stage columns with that role's lanes. */
export const WORK_BOARD_GROUP_BY_OPTIONS: readonly GroupBy[] = [
    ...WORK_BOARD_COLUMN_BY_OPTIONS,
    ...WORK_PERSON_ROLES,
];

export const getWorkBoardColumnBy = (groupBy: GroupBy): WorkBoardColumnBy =>
    groupBy === "none" || isWorkPersonRole(groupBy) ? "stage" : groupBy;

/** The header picker's value on Board, where no grouping is By stage. */
export const getWorkBoardGroupBy = (groupBy: GroupBy): GroupBy =>
    groupBy === "none" ? "stage" : groupBy;

const FINISHED_COLUMN_STATUSES: ReadonlyArray<[string, CloseStatus]> = [
    [DONE_STAGE_GROUP_ID, CloseStatus.DONE],
    [CANCELED_STAGE_GROUP_ID, CloseStatus.CANCELLED],
];

/** Done and Canceled columns the Status filter leaves out; none without a Status filter. */
const getStatusHiddenColumnIds = (
    closeStatuses: readonly CloseStatus[] | undefined,
): ReadonlySet<string> => {
    if (!closeStatuses?.length) {
        return new Set();
    }
    const allowedStatuses = new Set(closeStatuses);
    return new Set(
        FINISHED_COLUMN_STATUSES.filter(
            ([, status]) => !allowedStatuses.has(status),
        ).map(([columnId]) => columnId),
    );
};

/** Groupings whose empty columns never show, as List never lists those groups. */
const EMPTY_HIDDEN_COLUMN_BYS: ReadonlySet<WorkBoardColumnBy> = new Set([
    "client",
    "template",
]);

/** The applications in the rows, in order: work rows as they are, client and group rows' children. */
export const getWorkRows = (
    rows: readonly ClientsWorkOverviewRow[],
): ClientsWorkOverviewWorkRow[] =>
    rows.flatMap((row) => (row.rowType === "work" ? [row] : row.children));

export interface WorkBoardColumnsArgs {
    columnBy: WorkBoardColumnBy;
    /** The page's clients, in page order, with their applications. */
    workspaceRows: readonly ClientsWorkOverviewWorkspaceRow[];
    stages: readonly OrganizationCloseStage[];
    templateNames: ReadonlyMap<string, string>;
    isEmptyColumnsShown: boolean;
    getIcon?: (columnId: string) => ReactNode;
    /** The Status filter: Done and Canceled show only when it allows them; undefined shows both. */
    closeStatuses?: readonly CloseStatus[];
}

export interface WorkBoardColumns {
    columns: BoardColumnDef[];
    getPlacements: (row: ClientsWorkOverviewWorkRow) => BoardPlacement[];
}

const getGroups = ({
    columnBy,
    workspaceRows,
    stages,
    templateNames,
}: WorkBoardColumnsArgs): WorkRowGroup[] => {
    const rows = getWorkRows(workspaceRows);
    switch (columnBy) {
        case "stage":
            return getWorkStageGroups({ rows, stages });
        case "client":
            return workspaceRows.map((workspaceRow) => ({
                id: workspaceRow.id,
                title: workspaceRow.name,
                rows: workspaceRow.children,
            }));
        case "template":
            return getWorkTemplateGroups({ rows, templateNames });
        case "due_date":
            return getWorkDueDateGroups(rows);
    }
};

/**
 * The board's columns for its grouping, with the same ids, titles and order
 * as List's groups, and each application's place in them. Empty columns show
 * unless `isEmptyColumnsShown` is off; client and template never list them.
 */
export const getWorkBoardColumns = (
    args: WorkBoardColumnsArgs,
): WorkBoardColumns => {
    const statusHiddenColumnIds = getStatusHiddenColumnIds(args.closeStatuses);
    const groups = getGroups(args).filter(
        (group) => !statusHiddenColumnIds.has(group.id),
    );
    const isEmptyHidden =
        !args.isEmptyColumnsShown || EMPTY_HIDDEN_COLUMN_BYS.has(args.columnBy);
    const columnIdByRowId = new Map(
        groups.flatMap((group) =>
            group.rows.map((row): [string, string] => [row.id, group.id]),
        ),
    );
    return {
        columns: groups
            .filter((group) => !isEmptyHidden || group.rows.length > 0)
            .map((group) => ({
                id: group.id,
                title: group.title,
                icon: args.getIcon?.(group.id),
            })),
        getPlacements: (row) => {
            const columnId = columnIdByRowId.get(row.id);
            return columnId === undefined ? [] : [{ columnId, laneId: null }];
        },
    };
};
